/* eslint-disable no-cond-assign */
import { useCurrentUser, useCurrentUserId, useIsLoggedIn } from 'modules/Auth/hooks';
import { useListModelTypes } from 'modules/Playground/api/hooks/useListModelTypes';
import { useDataForPlayground } from 'modules/Playground/Components/PlaygroundModelSelector/useDataForPlayground';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { getFieldValueMap } from 'modules/Playground/utils';
import { PlaygroundMessage } from 'modules/Playground/store/messageStore';
import { grpcClient } from 'api/grpc/client';
import { StatusCode } from 'api/grpc/resources';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { appStorage } from 'utils/appStorage';
import { shuffleArray } from 'utils/array';
import { useBreakpoint } from 'utils/responsive';
import { breakpoints } from 'styles/utils';
import { constructNewModelInterfaceRequest } from '../useFetchModelOutput/constructNewModelInterfaceRequest';
import { getModelOutputFromGrpcResponse } from '../useFetchModelOutput/useFetchModelOutput';
import { MODEL_ID_FOR_SUGGESTIONS, PROMPTS_FALLBACK, QUERY_FALLBACK, STORAGE_KEY, extractAllJSON, generateQuery } from './constant';

const useModels = (modelId: string) => {
  const { data, isLoading } = useDataForPlayground({});
  let model: CF.API.Models.Model | null | undefined;
  if (data.total.length > 0 && !isLoading) {
    model = data.total?.find((_model) => _model.id === modelId) ?? null;
  }

  return model;
};

const useGRPCRequest = (promptSuggestionConfig: { model?: string; promptQuery?: string }) => {
  const currentUserId = useCurrentUserId();
  const model = useModels(promptSuggestionConfig.model ?? MODEL_ID_FOR_SUGGESTIONS);
  const { data: modelTypeFields } = useListModelTypes({ isEnabled: true });
  const currentUser = useCurrentUser() as { user: CF.API.Users.User } & CF.LocalUser;
  const defaultPromptQuery = promptSuggestionConfig.promptQuery || QUERY_FALLBACK;

  // Compute the field value map only when `model` or `modelTypeFields` change.
  const fieldValueMap = useMemo(() => {
    if (model && modelTypeFields) {
      return getFieldValueMap(model as unknown as CF.API.Models.Model, modelTypeFields.model_types as CF.API.Models.ModelTypesEntity[]);
    }
    return [{}, [], []];
  }, [model, modelTypeFields]);

  // Compute the request only if all the required data is present.
  const request = useMemo(() => {
    if (!model || !modelTypeFields || !currentUserId || !currentUser) {
      return null;
    }
    const message: PlaygroundMessage[] = [
      {
        role: 'user',
        content: currentUser ? generateQuery(currentUser?.user) : defaultPromptQuery,
        id: 'placeholder-id', // Replace with a proper ID generation mechanism
        file: [],
      },
    ];
    return constructNewModelInterfaceRequest({
      appId: model?.app_id ?? '',
      currentUserId,
      images: [],
      text: currentUser ? generateQuery(currentUser?.user) : defaultPromptQuery,
      userOrOrgId: model?.user_id ?? currentUserId,
      values: { model, modelVersion: model?.model_version, fieldValueMap } as unknown as PlaygroundConfig,
      chatMessage: message,
    });
  }, [model, modelTypeFields, currentUserId, fieldValueMap, currentUser]);

  if (model === null) {
    return { request: null, isError: true };
  }
  return { request, isError: false };
};

export const usePromptSuggestion = () => {
  const { promptSuggestionConfig } = useFeatureFlagsLD();
  const howManyPrompts = useBreakpoint(breakpoints.down('tab')) ? 2 : 4; // Adjust number of prompts based on screen size
  const [prompts, setPrompts] = useState<string[]>(Array.from({ length: howManyPrompts }).fill('') as string[]);
  const { request, isError } = useGRPCRequest(promptSuggestionConfig);
  const isLoggedIn = useIsLoggedIn();
  const hasFetched = useRef(false); // Track if the request has already been made
  const fallbackPrompts = promptSuggestionConfig?.defaultPrompts ?? PROMPTS_FALLBACK;

  const fetchPrompts = async () => {
    if (request && !hasFetched.current) {
      hasFetched.current = true; // Mark as fetched to prevent duplicate requests

      try {
        // Fetch model output
        const response = await grpcClient.postModelOutputsDirect(request);
        const responseObject = response.toObject();

        if (responseObject.status?.code !== StatusCode.SUCCESS) {
          throw new Error('Failed to fetch model output', {
            cause: responseObject,
          });
        }

        const output = getModelOutputFromGrpcResponse(responseObject.outputsList);
        const parseOutput = extractAllJSON(output as string)?.[0];
        const promptOutput =
          Array.isArray(parseOutput) || typeof parseOutput !== 'object' || parseOutput === null
            ? []
            : (parseOutput as Record<string, unknown>)?.prompts;

        if (Array.isArray(promptOutput) && promptOutput.length > 0) {
          savePrompts(promptOutput); // Save the first 4 prompts to storage
        } else {
          console.warn('No valid prompts found in the model output');
          setPrompts(fallbackPrompts);
        }
      } catch (error) {
        console.error('Error fetching prompts:', error);
        setPrompts(fallbackPrompts); // Fallback to default prompts
      }
    }
  };

  const getPrompts = (isErrorInRequest: boolean) => {
    const savedPrompts = getSavedPrompts();
    if (savedPrompts) {
      setPrompts(shuffleArray(savedPrompts)); // Shuffle and take the first 4 prompts
      hasFetched.current = true; // Mark as fetched to prevent duplicate request
    } else if (isErrorInRequest || !isLoggedIn) {
      setPrompts(fallbackPrompts); // Fallback to default prompts
    } else {
      fetchPrompts();
    }
  };

  const getSavedPrompts = () => {
    const storedPrompts = appStorage.get(STORAGE_KEY) as string[] | null;
    if (storedPrompts && storedPrompts.length > 0) {
      return shuffleArray(storedPrompts).slice(0, howManyPrompts); // Shuffle and take the first 4 prompts
    }
    return null;
  };

  const savePrompts = (newPrompts: string[]) => {
    appStorage.set(STORAGE_KEY, newPrompts);
    setPrompts(newPrompts.slice(0, howManyPrompts));
  };

  useEffect(() => {
    if (!hasFetched.current) {
      getPrompts(isError);
    }
  }, [isError, request, hasFetched.current, isLoggedIn]);

  return {
    prompts: prompts.map((prompt) => ({ title: prompt, id: uuid() })),
  };
};
